import styled, { keyframes } from "styled-components";
import ReactSVG from "react-svg";
import Link from "gatsby-link";

import { fromThemeProps } from "../../../helpers/utilities";
import media from "../../../helpers/media";

export const H1 = styled.h1`
  color: ${fromThemeProps("h1Color")};
  font-family: ${fromThemeProps("h1Font")};
  font-size: ${(props) => props.mobileFontSize || fromThemeProps("h1FontSizeMobile")};
  font-weight: ${fromThemeProps("h1Weight")};
  letter-spacing: 8px;
  line-height: 1.5;
  text-transform: uppercase;
  text-align: center;

  span {
    position: relative;
    z-index: 1;
  }

  span::before {
    content: "";
    position: absolute;
    width: calc(100% - 5px);
    top: calc(${(props) => props.mobileFontSize || fromThemeProps("h1FontSizeMobile")} - 5px);
    border-bottom: 8px solid ${fromThemeProps("yellow")};

    z-index: -1;
  }

  ${media.xs`
    font-size: ${fromThemeProps("h1FontSizeDesktop")};
    span::before {
      top: calc(${fromThemeProps("h1FontSizeDesktop")} - 5px)
    }
  `}
`;

export const H2 = styled.h2`
  color: ${(props) => props.color || fromThemeProps("h2Color")};
  font-family: ${fromThemeProps("h2Font")};
  font-size: ${fromThemeProps("h2FontSizeMobile")};
  font-weight: ${fromThemeProps("h2Weight")};
  line-height: ${fromThemeProps("largeLineHeight")};
  margin-bottom: 1.5rem;

  ${media.xs`
    font-size: ${fromThemeProps("h2FontSizeDesktop")};
  `}
`;

export const H3 = styled.h3`
  margin-bottom: 1.5rem;

  color: ${fromThemeProps("h3Color")};
  font-family: ${fromThemeProps("h3Font")};
  font-size: ${fromThemeProps("h3FontSizeMobile")};
  font-weight: ${fromThemeProps("h3Weight")};
  line-height: ${fromThemeProps("largeLineHeight")};

  ${media.xs`
    font-size: ${fromThemeProps("h3FontSizeDesktop")};
  `}
`;

export const H4 = styled.h4`
  color: ${fromThemeProps("h4Color")};
  font-family: ${fromThemeProps("h4Font")};
  font-size: ${fromThemeProps("h4FontSizeMobile")};
  font-weight: ${fromThemeProps("h4Weight")};

  ${media.xs`
    font-size: ${fromThemeProps("h4FontSizeDesktop")};
  `}
`;
export const H5 = styled.h5`
  color: ${fromThemeProps("h5Color")};
  font-family: ${fromThemeProps("h5Font")};
  font-size: ${fromThemeProps("h5FontSizeMobile")};
  font-weight: ${fromThemeProps("h5Weight")};

  ${media.xs`
    font-size: ${fromThemeProps("h5FontSizeDesktop")};
  `}
`;

export const P = styled.p`
  color: ${(props) => props.color || fromThemeProps("pColor")};
  font-family: ${fromThemeProps("pFont")};
  font-size: ${fromThemeProps("pFontSizeMobile")};
  font-weight: ${(props) => props.weight || fromThemeProps("pWeight")};
  line-height: ${fromThemeProps("largeLineHeight")};
  margin-bottom: 1.5rem;
  overflow-wrap: break-word;

  ${media.xs`
    font-size: ${fromThemeProps("pFontSizeDesktop")};
  `}
`;

export const A = styled.a`
  color: ${fromThemeProps("aColor")};
  font-family: ${fromThemeProps("aFont")};
  font-size: ${fromThemeProps("aFontSizeMobile")};
  font-weight: ${fromThemeProps("aWeight")};
  text-decoration: underline;

  ${media.xs`
    font-size: ${fromThemeProps("aFontSizeDesktop")};
  `}

  &:hover {
    font-weight: ${fromThemeProps("aHoverWeight")};
  }
`;

export const PrimaryButton = styled(Link)`
  background: ${fromThemeProps("primaryButtonBackground")};
  padding: ${fromThemeProps("buttonPadding")};
  margin-bottom: 1.5rem;

  color: ${fromThemeProps("primaryButtonColor")};
  font-family: ${fromThemeProps("buttonFont")};
  font-size: ${fromThemeProps("buttonFontSizeMobile")};
  font-weight: ${fromThemeProps("buttonWeight")};
  letter-spacing: 3px;
  text-transform: uppercase;

  &:hover {
    background: ${fromThemeProps("primaryButtonHover")};
  }

  ${media.xs`
    display: ${(props) => {
      if (props.hideDesktop) {
        return "none";
      }
      return "flex";
    }}
    font-size: ${fromThemeProps("buttonFontSizeDesktop")};
  `}
`;

export const SecondaryButton = styled(PrimaryButton)`
  background: ${fromThemeProps("secondaryButtonBackground")};
  border: 3px solid ${fromThemeProps("yellow")};

  color: ${(props) => props.color || fromThemeProps("secondaryButtonColor")};

  &:hover {
    background: ${fromThemeProps("secondaryButtonHover")};
  }
`;

export const FooterText = styled(P)`
  padding: 1em;

  color: ${fromThemeProps("gray")};
  font-size: ${fromThemeProps("footerFontSizeMobile")};
  text-align: center;

  ${media.xs`
    font-size: ${fromThemeProps("footerFontSizeDesktop")};
  `}
`;

export const Ol = styled.ol``;

export const Ul = styled.ul`
  color: ${(props) => props.color || fromThemeProps("pColor")};
  font-family: ${fromThemeProps("pFont")};
  font-size: ${fromThemeProps("pFontSizeMobile")};
  font-weight: ${(props) => props.weight || fromThemeProps("pWeight")};
  line-height: ${fromThemeProps("largeLineHeight")};
  margin-bottom: 1.5rem;
  overflow-wrap: break-word;
  padding-left: 2em;
  list-style: disc;

  ${media.xs`
    font-size: ${fromThemeProps("pFontSizeDesktop")};
  `}
`;

export const Li = styled.li`
  display: list-item;
`;

export const Strong = styled.strong`
  font-weight: 700;
`;

export const HeaderSVG = styled(ReactSVG)`
  width: 75px;
  padding: 1em;
`;

export const CommitmentLogo = styled.img`
  height: 100px;
  padding: 1em;
  max-width: 200px;

  ${media.xs`
    padding: 0.75em 1em;
    height: 100px;
    max-width: 175px;
  `}
`;

const rotate = keyframes`
  100% {
   transform: rotate(360deg);
  }
`;
export const AwesomeLoading = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  height: 0;
  width: 0;
  padding: 10px;
  border: 3px solid rgba(241, 78, 38, 0.2);
  margin: -20px 0 0 -13px;
  border-right-color: rgba(241, 78, 38, 1);
  border-radius: 50%;
  animation: ${rotate} 0.8s infinite linear;
`;



// WEBPACK FOOTER //
// ./src/styles/styledComponents/elements/index.js